'use client';

import { ChevronDown } from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../../../utilities/classname';
import { closestFrom } from '../../../../utilities/dom/closest-from';
import { hidePopoversIn } from '../../../../utilities/dom/popover';
import { NavPanelController } from './nav-panel-controller';

/** One full-width panel: its content is rendered on the server and handed in. */
export type NavPanel = {
  /** The region's DOM id, which its trigger's `aria-controls` names. */
  id: string;
  /** The nav label: the region's accessible name. */
  label: string;
  content: ReactNode;
};

type NavPanelContextValue = {
  controller: NavPanelController;
  openId: string | null;
};

const NavPanelContext = createContext<NavPanelContextValue | null>(null);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focusable elements under `root` that are actually on screen (the drawer toggle is `display: none` from `xl`). */
const focusablesIn = (root: ParentNode): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((element) => typeof element.checkVisibility !== 'function' || element.checkVisibility());

const triggerFor = (root: ParentNode, panelId: string): HTMLElement | undefined =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-nav-panel-trigger]'))
    .find((trigger) => trigger.getAttribute('aria-controls') === panelId);

const isMouse = (event: PointerEvent): boolean => event.pointerType === 'mouse';

/**
 * The client shell of a navbar's full-width panels: the banner and nav
 * surface arrive as `children` and each panel's content as a prop, all
 * rendered on the server, so its own JavaScript is the open/close behaviour.
 * A panel's content may hold small client islands of its own (a location's
 * live "Today" line, `VisitDetails`), which hydrate where they sit.
 *
 * It takes over the navbar's `<nav aria-label="Primary">` so the bar and the
 * panel form one hover region, and puts the panel directly under it
 * (`top-full`), inside the `<header>`. Every panel stays in the DOM, `hidden`
 * until shown: its links are in the served HTML for crawlers, and its lazy
 * images are not fetched until it opens.
 *
 * - Hover opens after a short intent delay and switches instantly between
 *   open panels; leaving the header closes (see `NavPanelController`). Only a
 *   mouse hovers: touch and pen go by click, so a tap never opens and closes
 *   in one gesture.
 * - Escape closes, returning focus to the trigger if focus was in the panel
 *   or on the trigger. Tab from an open trigger enters its panel, and Tab off
 *   the panel's end moves on to the next item in the bar; focus leaving the
 *   header closes.
 * - Following a link closes, and so does a click anywhere in the bar that is
 *   not a trigger (the search box, a CTA, another dropdown); opening a panel
 *   closes any list or columns dropdown (a popover) left open in the bar.
 * - Under a header that does not stick, scrolling the page closes: the panel
 *   would scroll away with the header, open and out of sight.
 * - The panel's height follows its content (a `ResizeObserver` on the shown
 *   region, so late-loading content is measured too; without one it is
 *   `auto`) and is capped at the space left in the viewport, scrolling inside
 *   beyond that: under a sticky header a tall panel could not otherwise be
 *   read to the end.
 * - While a panel is shown the navbar is raised to `z-50`, above page sections
 *   that stack at z-30/z-40; closed it keeps the `z-10` it always had. The
 *   backdrop has no height while closed, so it never adds to the page's.
 * - The panels sit after the whole bar in the DOM (one shell animates
 *   between them), so each trigger's `<li>` claims its panel with
 *   `aria-owns`: a screen reader reads an open panel straight after its
 *   trigger. The navbars set that, as they render the `<li>`.
 * - The panel and backdrop are desktop-only, like the menu that opens them
 *   (`xl`): one left open when the window narrows past it simply disappears.
 */
export const NavPanelRoot: React.FC<{
  panels: readonly NavPanel[];
  /** The panel's background and text, from `navPanelSurfaceClass`. */
  surfaceClassName: string;
  children: ReactNode;
}> = ({ panels, surfaceClassName, children }) => {
  const orderKey = panels.map(({ id }) => id).join(' ');
  const [controller] = useState(() => new NavPanelController(orderKey.split(' ')));
  const { openId, shownId, motion } = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  /** The shown region's height; unset until a `ResizeObserver` has measured it, or for good without one. */
  const [height, setHeight] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();

  useEffect(() => { controller.setOrder(orderKey.split(' ')); }, [controller, orderKey]);
  useEffect(() => () => controller.dispose(), [controller]);

  useEffect(() => {
    const inner = innerRef.current;
    if (inner === null || typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(() => setHeight(inner.offsetHeight));
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (openId === null || root === null) { return undefined; }

    const trigger = triggerFor(root, openId);
    const shell = root.querySelector('[data-nav-panel-shell]');
    const sticky = root.closest('[data-sticky]') !== null;

    const fitViewport = () => setMaxHeight(Math.max(0, window.innerHeight - root.getBoundingClientRect().bottom));
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') { return; }
      const active = document.activeElement;
      const hadFocus = active !== null && (active === trigger || shell?.contains(active) === true);
      controller.close();
      if (hadFocus) { trigger?.focus(); }
    };
    const onScroll = () => controller.close();

    hidePopoversIn(root);
    fitViewport();
    window.addEventListener('resize', fitViewport);
    document.addEventListener('keydown', onKeyDown);
    if (!sticky) { window.addEventListener('scroll', onScroll, { passive: true }); }
    return () => {
      window.removeEventListener('resize', fitViewport);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll);
    };
  }, [controller, openId]);

  const context = useMemo<NavPanelContextValue>(() => ({ controller, openId }), [controller, openId]);

  const onPointerOver = (event: PointerEvent<HTMLElement>) => {
    if (!isMouse(event)) { return; }
    if (closestFrom(event.target, '[data-nav-panel-shell]') !== null) {
      controller.cancelClose();
      return;
    }
    const item = closestFrom(event.target, '[data-nav-menu] > li');
    if (item !== null && item.querySelector('[data-nav-panel-trigger]') === null) {
      controller.hoverPlainItem();
    }
  };

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const inPanel = closestFrom(event.target, '[data-nav-panel-shell]') !== null;
    const onTrigger = closestFrom(event.target, '[data-nav-panel-trigger]') !== null;
    if (closestFrom(event.target, 'a[href]') !== null || (!inPanel && !onTrigger)) { controller.close(); }
  };

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    // Only a focus move to somewhere else on the page counts: a click on the
    // panel's own whitespace blurs to nothing and must not close it.
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next)) { controller.close(); }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const region = event.key === 'Tab' && openId !== null ? document.getElementById(openId) : null;
    const trigger = region === null || openId === null ? undefined : triggerFor(event.currentTarget, openId);
    const inPanel = region === null ? [] : focusablesIn(region);
    const first = inPanel.at(0);
    const last = inPanel.at(-1);
    if (trigger === undefined || first === undefined || last === undefined) { return; }

    if (!event.shiftKey && event.target === trigger) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && event.target === first) {
      event.preventDefault();
      trigger.focus();
    } else if (!event.shiftKey && event.target === last) {
      const bar = focusablesIn(event.currentTarget).filter((element) => !element.closest('[data-nav-panel-shell]'));
      // The first control after the trigger that actually takes focus: where
      // there is no `checkVisibility` (Safari before 17.4), a hidden one, such
      // as the drawer toggle, is still listed, and focusing it does nothing.
      const next = bar.slice(bar.indexOf(trigger) + 1).find((element) => {
        element.focus();
        return document.activeElement === element;
      });
      if (next !== undefined) {
        event.preventDefault();
        controller.close();
      }
    }
  };

  return (
    <NavPanelContext.Provider value={context}>
      <div ref={rootRef} data-nav-root="" className={cn('relative', shownId === null ? 'z-10' : 'z-50')}>
        <div
          aria-hidden="true"
          data-nav-panel-backdrop=""
          className={cn(
            'absolute inset-x-0 top-full bg-base-content/25 transition-opacity duration-300 motion-reduce:transition-none max-xl:hidden',
            shownId === null ? 'h-0' : 'h-screen',
            openId === null ? 'pointer-events-none opacity-0' : 'opacity-100',
          )}
          onClick={() => controller.close()}
        />

        <nav
          aria-label="Primary"
          onPointerEnter={(event) => { if (isMouse(event)) { controller.cancelClose(); } }}
          onPointerLeave={(event) => { if (isMouse(event)) { controller.leave(); } }}
          onPointerOver={onPointerOver}
          onClick={onClick}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        >
          {children}

          <div
            data-nav-panel-shell=""
            className={cn(
              'nav-panel-shell absolute inset-x-0 top-full overflow-hidden border-b border-current/15 shadow-box max-xl:hidden',
              surfaceClassName,
              openId === null && 'invisible',
            )}
            style={{ height: openId === null ? 0 : (height ?? 'auto') }}
          >
            <div
              ref={innerRef}
              data-nav-motion={motion}
              className="overflow-y-auto overscroll-contain"
              style={{ maxHeight }}
            >
              {panels.map(({ id, label, content }) => (
                <div key={id} id={id} role="region" aria-label={label} hidden={id !== shownId}>
                  {content}
                </div>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </NavPanelContext.Provider>
  );
};

/**
 * A nav item that opens a full-width panel: a button (Enter/Space toggle it),
 * with a chevron that turns while its panel is open. Rendered inside the
 * navbar's menu `<li>`, so it takes the menu item's look, and inside a
 * {@link NavPanelRoot}, whose state it reads.
 */
export const NavPanelTrigger: React.FC<{
  panelId: string;
  label: string;
  className?: string;
}> = ({ panelId, label, className }) => {
  const context = useContext(NavPanelContext);
  if (context === null) { throw new Error('NavPanelTrigger must be rendered inside a NavPanelRoot'); }

  const { controller, openId } = context;
  const open = openId === panelId;

  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      data-nav-panel-trigger=""
      className={cn(className, open && 'bg-base-content/10')}
      onPointerEnter={(event) => { if (isMouse(event)) { controller.hoverTrigger(panelId); } }}
      onPointerLeave={(event) => { if (isMouse(event)) { controller.cancelIntent(); } }}
      onClick={() => controller.clickTrigger(panelId)}
    >
      {label}
      <ChevronDown
        aria-hidden="true"
        className={cn('size-4 opacity-60 transition-transform duration-200 motion-reduce:transition-none', open && 'rotate-180')}
      />
    </button>
  );
};
